import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Save, Crown, ArrowRight } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [detail, setDetail] = useState(user?.preferences?.detail_level || "normal");
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/me/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/profile", { name, detail_level: detail });
      setUser(r.data);
      toast.success("Profil mis à jour");
    } catch {
      toast.error("Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5 sm:p-10 max-w-2xl">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-royal mb-2">Compte</p>
      <h1 className="font-serif text-4xl text-navy mb-8">Mon profil</h1>

      <div className="bg-white border border-border rounded-lg p-8 space-y-6">
        <div className="flex items-center gap-4">
          {user?.picture ? <img src={user.picture} alt="" className="h-16 w-16 rounded-full" /> : <div className="h-16 w-16 rounded-full bg-navy text-white flex items-center justify-center text-2xl font-serif">{name?.[0]?.toUpperCase()}</div>}
          <div>
            <p className="font-medium text-navy">{user?.email}</p>
            <p className="text-sm text-slate-400">Membre {user?.is_admin && "· Administrateur"}</p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-navy block mb-2">Nom complet</label>
          <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2.5 border border-border rounded-md focus:ring-2 focus:ring-royal/40 focus:outline-none" />
        </div>

        <div>
          <label className="text-sm font-medium text-navy block mb-2">Niveau de détail des réponses IA</label>
          <div className="flex gap-2">
            {[["simple", "Simple"], ["normal", "Normal"], ["complet", "Complet"]].map(([v, l]) => (
              <button key={v} data-testid={`detail-${v}`} onClick={() => setDetail(v)} className={`flex-1 py-2.5 rounded-md text-sm font-medium border transition-all duration-200 ${detail === v ? "bg-navy text-white border-navy" : "border-border text-slate-600 hover:border-navy/40"}`}>{l}</button>
            ))}
          </div>
        </div>

        <button data-testid="profile-save" onClick={save} disabled={saving} className="flex items-center gap-2 bg-royal hover:bg-royal/90 text-white px-6 py-2.5 rounded-md font-medium disabled:opacity-50 transition-all duration-200">
          <Save size={16} /> {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </div>

      <div className="mt-6 bg-white border border-border rounded-lg p-6" data-testid="subscription-card">
        <div className="flex items-center gap-2 mb-3">
          <Crown size={18} className="text-gold" />
          <h3 className="font-serif text-xl text-navy">Abonnement</h3>
        </div>
        {usage?.is_premium ? (
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-gold bg-gold/10 px-3 py-1 rounded-full">
              <Crown size={12} /> Premium {usage.subscription_plan === "annual" ? "Annuel" : "Mensuel"}
            </span>
            <p className="text-sm text-slate-600 mt-3">
              Accès illimité actif jusqu'au{" "}
              <span className="font-medium text-navy">{new Date(usage.subscription_expires_at).toLocaleDateString("fr-FR")}</span>.
            </p>
            <button data-testid="manage-premium-btn" onClick={() => navigate("/premium")} className="mt-4 text-sm text-royal font-medium hover:underline inline-flex items-center gap-1">
              Gérer / prolonger <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-600 mb-4">Vous utilisez l'offre <span className="font-medium text-navy">Gratuite</span> (5 questions/jour, 2 documents/mois). Passez à Premium pour un accès illimité.</p>
            <button data-testid="go-premium-btn" onClick={() => navigate("/premium")} className="flex items-center gap-2 bg-gold hover:bg-gold/90 text-navy px-6 py-2.5 rounded-md font-semibold transition-all duration-200">
              <Crown size={16} /> Passer à Premium
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-border rounded-lg p-6 text-sm text-slate-500">
        <p className="font-medium text-navy mb-1">Vos données (RGPD)</p>
        <p>Vos conversations et documents sont conservés de manière sécurisée. Contactez-nous pour exporter ou supprimer vos données.</p>
      </div>
    </div>
  );
}
